import { KeyRound, Lock, Mail } from '@hostingportal/icons'
import { Input } from '@hostingportal/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { allowedDomainMessage, isAllowedEmail, writeAuthSession } from '../../lib/auth'
import {
  AuthError as SignInError,
  NewPasswordRequiredError,
  completeNewPasswordChallenge,
  signIn,
} from '../../lib/auth-provider'
import { Trans, t } from '../../lib/i18n'
import { isMock } from '../../lib/runtime'
import {
  AuthActionRow,
  AuthError,
  AuthExperience,
  AuthField,
  AuthForm,
  PasswordChecklist,
  PasswordField,
  authInputClassName,
  passwordIssue,
  passwordsMatch,
} from '../../modules/auth/auth-experience'

/**
 * Login page. Until the hosted identity provider is connected, this keeps the
 * cockpit behind an allowlisted email-domain session gate.
 */
export const Route = createFileRoute('/_auth/login')({
  component: LoginPage,
})

function LoginPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordChallenge, setPasswordChallenge] = useState<{
    email: string
    session: string
  } | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const isSettingPassword = Boolean(passwordChallenge)

  return (
    <AuthExperience
      icon={isSettingPassword ? KeyRound : Lock}
      title={isSettingPassword ? <Trans>Set your password</Trans> : <Trans>Welcome back</Trans>}
      description={
        isSettingPassword ? (
          <Trans>Create a permanent password to finish signing in.</Trans>
        ) : (
          <Trans>Use your workspace email and password. Invited users can start here too.</Trans>
        )
      }
    >
      <AuthForm
        onSubmit={async (e) => {
          e.preventDefault()
          if (busy) return
          if (passwordChallenge) {
            const passwordError = passwordIssue(newPassword)
            if (passwordError) {
              setError(passwordError)
              return
            }
            if (!passwordsMatch(newPassword, confirmPassword)) {
              setError(t('Passwords must match.'))
              return
            }
            if (isMock) {
              writeAuthSession({ email: passwordChallenge.email })
              navigate({ to: '/' })
              return
            }
            setBusy(true)
            try {
              const session = await completeNewPasswordChallenge({
                email: passwordChallenge.email,
                session: passwordChallenge.session,
                newPassword,
              })
              writeAuthSession(session)
              navigate({ to: '/' })
            } catch (err) {
              setError(
                err instanceof SignInError
                  ? t(err.message)
                  : t('Could not set this password. Try again or ask for a fresh invite.'),
              )
            } finally {
              setBusy(false)
            }
            return
          }

          const trimmedEmail = email.trim()
          if (!isAllowedEmail(trimmedEmail)) {
            setError(t(allowedDomainMessage()))
            return
          }
          if (!password) {
            setError(t('Enter your password.'))
            return
          }
          // Local dev (and mock) skip the hosted IdP: mint a session directly so
          // the form works without Cognito. The backend's local-dev verifier
          // ignores the token; the workspace id selects the active brand.
          if (isMock || import.meta.env.DEV) {
            writeAuthSession({
              email: trimmedEmail,
              name: trimmedEmail.split('@')[0],
              ...(isMock
                ? {}
                : {
                    idToken: 'local-dev-token',
                    role: 'owner',
                    workspaceId:
                      (import.meta.env.VITE_HOSTINGPORTAL_DEV_WORKSPACE_ID as string | undefined) ??
                      'ws_c48bb781-dde4-4057-bf54-b89e69fe2bbd',
                  }),
            })
            navigate({ to: '/' })
            return
          }
          setBusy(true)
          try {
            const session = await signIn(trimmedEmail, password)
            writeAuthSession(session)
            navigate({ to: '/' })
          } catch (err) {
            if (err instanceof NewPasswordRequiredError) {
              setPasswordChallenge({ email: err.email, session: err.session })
              setEmail(err.email)
              setPassword('')
              setError(null)
              return
            }
            setError(
              err instanceof SignInError
                ? t(err.message)
                : t('Could not sign in. Check your account and try again.'),
            )
          } finally {
            setBusy(false)
          }
        }}
      >
        <AuthField label={<Trans>Email</Trans>} icon={Mail}>
          <Input
            type="email"
            placeholder={t('you@company.com')}
            aria-label={t('Email')}
            value={passwordChallenge?.email ?? email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (error) setError(null)
            }}
            invalid={Boolean(error) && !isAllowedEmail(email)}
            autoComplete="email"
            className={authInputClassName}
            disabled={isSettingPassword}
            required
          />
        </AuthField>
        {passwordChallenge ? (
          <>
            <PasswordField
              label={<Trans>New password</Trans>}
              value={newPassword}
              onChange={(value) => {
                setNewPassword(value)
                if (error) setError(null)
              }}
              invalid={Boolean(error) && Boolean(passwordIssue(newPassword))}
              autoComplete="new-password"
            />
            <PasswordField
              label={<Trans>Confirm password</Trans>}
              value={confirmPassword}
              onChange={(value) => {
                setConfirmPassword(value)
                if (error) setError(null)
              }}
              invalid={Boolean(error) && !passwordsMatch(newPassword, confirmPassword)}
              autoComplete="new-password"
              placeholder={t('Confirm password')}
              ariaLabel={t('Confirm password')}
            />
            <PasswordChecklist password={newPassword} confirmPassword={confirmPassword} />
          </>
        ) : (
          <PasswordField
            value={password}
            onChange={(value) => {
              setPassword(value)
              if (error) setError(null)
            }}
            invalid={Boolean(error) && !password}
            autoComplete="current-password"
          />
        )}
        {error ? <AuthError>{error}</AuthError> : null}
        <AuthActionRow
          mode={passwordChallenge ? 'signup' : 'login'}
          busy={busy}
          showSwitch={!passwordChallenge}
          label={passwordChallenge ? <Trans>Set password and sign in</Trans> : undefined}
          busyLabel={passwordChallenge ? <Trans>Setting password</Trans> : undefined}
        />
      </AuthForm>
    </AuthExperience>
  )
}
